import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { fetchCanonicalOrganizationLogos, subscribeCanonicalOrganizationLogoWrites } from '@/hooks/useCanonicalOrganizationLogo';

export interface MSPCustomer {
  id: string;
  customer_org_id: string;
  customer_org: {
    id: string;
    name: string;
    slug: string;
    logo_url?: string | null;
    settings?: {
      domain?: string | null;
    } | null;
  };
  max_suppliers: number;
  status: string;
  provisioned_at: string;
}

export interface MSPAccessInfo {
  isMSP: boolean;
  isMSPMember: boolean;
  mspOrg: { id: string; name: string } | null;
  customers: MSPCustomer[];
  customerCount: number;
  isViewingAsCustomer: boolean;
  currentCustomerOrgId: string | null;
  accessLevel: 'admin' | 'staff' | null;
  /** ENT-5657: canonical customer-logo hydration failed; access and customers are still authoritative. */
  customerLogoError: Error | null;
}

interface UseMSPAccessOptions {
  includeCustomers?: boolean;
}

const MSP_ACCESS_CACHE_TTL_MS = 30_000;
const mspAccessCache = new Map<string, { data: MSPAccessInfo; expiresAt: number }>();
const mspAccessInflight = new Map<string, { request: Promise<MSPAccessInfo>; includeCustomers: boolean }>();
// ENT-6445: bumped by every customer logo write. A with-customers result read across a bump may
// hold the previous mark, so it is neither cached nor kept by the hook that awaited it.
let mspAccessLogoEpoch = 0;
const mspAccessLogoWriteListeners = new Set<(organizationId: string) => void>();

function createDefaultMSPAccess(): MSPAccessInfo {
  return {
    isMSP: false,
    isMSPMember: false,
    mspOrg: null,
    customers: [],
    customerCount: 0,
    isViewingAsCustomer: false,
    currentCustomerOrgId: null,
    accessLevel: null,
    customerLogoError: null,
  };
}

function buildMSPAccessCacheKey(params: {
  userId: string;
  organizationId: string;
  isMspContext: boolean;
  includeCustomers: boolean;
}) {
  return [
    params.userId,
    params.organizationId,
    params.isMspContext ? 'msp-context' : 'org-context',
    params.includeCustomers ? 'with-customers' : 'without-customers',
  ].join(':');
}

function withoutCustomerLogos(customers: MSPCustomer[]): MSPCustomer[] {
  return customers.map((customer) => ({ ...customer, customer_org: { ...customer.customer_org, logo_url: null } }));
}

function toCustomerLogoError(error: unknown) {
  return error instanceof Error ? error : new Error('Canonical organisation logo hydration failed');
}

async function hydrateCustomerCanonicalLogos(
  customers: MSPCustomer[],
): Promise<{ customers: MSPCustomer[]; logoError: Error | null }> {
  const organizationIds = customers
    .map((customer) => customer.customer_org?.id)
    .filter((organizationId): organizationId is string => typeof organizationId === 'string' && organizationId.length > 0);
  if (organizationIds.length === 0) {
    return { customers, logoError: null };
  }
  // ENT-5657: a logo-resolver failure never removes MSP access or customers. Raw
  // logos are never kept either: every customer shows initials and the caller
  // receives an explicit logo error state.
  try {
    const canonicalLogos = await fetchCanonicalOrganizationLogos(organizationIds);
    return {
      customers: customers.map((customer) => {
        const canonicalLogo = canonicalLogos.get(customer.customer_org.id);
        if (!canonicalLogo) throw new Error('Canonical organisation logo batch resolver omitted an MSP customer');
        return { ...customer, customer_org: { ...customer.customer_org, logo_url: canonicalLogo.logoUrl } };
      }),
      logoError: null,
    };
  } catch (error) {
    return {
      customers: withoutCustomerLogos(customers),
      logoError: toCustomerLogoError(error),
    };
  }
}

async function fetchMSPAccessInfo(params: {
  userId: string;
  organizationId: string;
  organizationName: string;
  isMspContext: boolean;
  includeCustomers: boolean;
}): Promise<MSPAccessInfo> {
  const {
    userId,
    organizationId,
    organizationName,
    isMspContext,
    includeCustomers,
  } = params;

  if (isMspContext) {
    const { data: mspRelation } = await supabase
      .from('msp_customer_relationships')
      .select(`
        id,
        msp_org_id,
        max_suppliers
      `)
      .eq('customer_org_id', organizationId)
      .eq('status', 'active')
      .maybeSingle();

    if (mspRelation) {
      const { data: mspOrg } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('id', mspRelation.msp_org_id)
        .single();

      const { data: mspMembership } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', mspRelation.msp_org_id)
        .eq('user_id', userId)
        .maybeSingle();

      return {
        isMSP: false,
        isMSPMember: true,
        mspOrg: mspOrg || null,
        customers: [],
        customerCount: 0,
        isViewingAsCustomer: true,
        currentCustomerOrgId: organizationId,
        accessLevel: mspMembership?.role === 'admin' ? 'admin' : 'staff',
        customerLogoError: null,
      };
    }
  }

  const { data: mspAccount } = await supabase
    .from('msp_accounts')
    .select('id, organization_id, is_active')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .maybeSingle();

  if (mspAccount) {
    const { data: membership } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .maybeSingle();

    const isAdmin = membership?.role === 'admin';
    let customerRelations: MSPCustomer[] = [];
    let customerLogoError: Error | null = null;
    let customerCount = 0;

    if (isAdmin) {
      const { count, error: countError } = await supabase
        .from('msp_customer_relationships')
        .select('id', { count: 'exact', head: true })
        .eq('msp_org_id', organizationId)
        .eq('status', 'active');

      if (countError) {
        throw countError;
      }

      customerCount = count ?? 0;

      if (includeCustomers) {
        const pageSize = 500;
        const pagedCustomerRelations: MSPCustomer[] = [];

        for (let start = 0; start < customerCount; start += pageSize) {
          const end = Math.min(start + pageSize - 1, customerCount - 1);
          const { data, error } = await supabase
            .from('msp_customer_relationships')
            .select(`
              id,
              customer_org_id,
              max_suppliers,
              status,
              provisioned_at,
              customer_org:organizations!msp_customer_relationships_customer_org_id_fkey(id, name, slug)
            `)
            .eq('msp_org_id', organizationId)
            .eq('status', 'active')
            .range(start, end);

          if (error) {
            throw error;
          }

          pagedCustomerRelations.push(...(((data || []) as MSPCustomer[]).filter((customer) => customer.customer_org)));
        }

        ({ customers: customerRelations, logoError: customerLogoError } = await hydrateCustomerCanonicalLogos(pagedCustomerRelations));
      }
    } else {
      const { data: assignments } = await supabase
        .from('msp_user_assignments')
        .select('customer_org_id')
        .eq('msp_org_id', organizationId)
        .eq('user_id', userId);

      const assignedOrgIds = (assignments || []).map((assignment) => assignment.customer_org_id);
      customerCount = assignedOrgIds.length;

      if (includeCustomers && assignedOrgIds.length > 0) {
        const pageSize = 200;
        const pagedCustomerRelations: MSPCustomer[] = [];

        for (let start = 0; start < assignedOrgIds.length; start += pageSize) {
          const customerOrgIdChunk = assignedOrgIds.slice(start, start + pageSize);
          const { data, error } = await supabase
            .from('msp_customer_relationships')
            .select(`
              id,
              customer_org_id,
              max_suppliers,
              status,
              provisioned_at,
              customer_org:organizations!msp_customer_relationships_customer_org_id_fkey(id, name, slug)
            `)
            .eq('msp_org_id', organizationId)
            .eq('status', 'active')
            .in('customer_org_id', customerOrgIdChunk);

          if (error) {
            throw error;
          }

          pagedCustomerRelations.push(...(((data || []) as MSPCustomer[]).filter((customer) => customer.customer_org)));
        }

        ({ customers: customerRelations, logoError: customerLogoError } = await hydrateCustomerCanonicalLogos(pagedCustomerRelations));
      }
    }

    return {
      isMSP: true,
      isMSPMember: true,
      mspOrg: { id: organizationId, name: organizationName },
      customers: customerRelations,
      customerCount,
      isViewingAsCustomer: false,
      currentCustomerOrgId: null,
      accessLevel: isAdmin ? 'admin' : 'staff',
      customerLogoError,
    };
  }

  const { data: mspRelation } = await supabase
    .from('msp_customer_relationships')
    .select(`
      id,
      msp_org_id,
      max_suppliers
    `)
    .eq('customer_org_id', organizationId)
    .eq('status', 'active')
    .maybeSingle();

  if (mspRelation) {
    const { data: mspMembership } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', mspRelation.msp_org_id)
      .eq('user_id', userId)
      .maybeSingle();

    if (mspMembership) {
      const { data: mspOrg } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('id', mspRelation.msp_org_id)
        .single();

      return {
        isMSP: false,
        isMSPMember: true,
        mspOrg: mspOrg || null,
        customers: [],
        customerCount: 0,
        isViewingAsCustomer: true,
        currentCustomerOrgId: organizationId,
        accessLevel: mspMembership.role === 'admin' ? 'admin' : 'staff',
        customerLogoError: null,
      };
    }
  }

  return createDefaultMSPAccess();
}

function includesCustomerOrganization(data: MSPAccessInfo, organizationId: string) {
  return data.customers.some((customer) => customer.customer_org?.id === organizationId);
}

/**
 * ENT-6445: after an organisation logo write, drop every cached access result that carries that
 * organisation as a customer, abandon in-flight customer reads, and let mounted consumers showing
 * it refetch in the background. Runs from `invalidateCanonicalOrganizationLogo`.
 */
export function invalidateMSPAccessOrganizationLogo(organizationId: string) {
  mspAccessLogoEpoch += 1;
  for (const [cacheKey, entry] of mspAccessCache) {
    if (includesCustomerOrganization(entry.data, organizationId)) mspAccessCache.delete(cacheKey);
  }
  for (const [cacheKey, inflight] of mspAccessInflight) {
    if (inflight.includeCustomers) mspAccessInflight.delete(cacheKey);
  }
  for (const listener of mspAccessLogoWriteListeners) listener(organizationId);
}

subscribeCanonicalOrganizationLogoWrites(invalidateMSPAccessOrganizationLogo);

function loadMSPAccessInfo(cacheKey: string, params: Parameters<typeof fetchMSPAccessInfo>[0]): Promise<MSPAccessInfo> {
  const inflight = mspAccessInflight.get(cacheKey);
  if (inflight) return inflight.request;

  const startedEpoch = mspAccessLogoEpoch;
  const request = fetchMSPAccessInfo(params).then((data) => {
    // A degraded logo result is never cached, so the next check retries it; nor is a customer
    // result that a logo write overtook.
    if (!data.customerLogoError && (!params.includeCustomers || startedEpoch === mspAccessLogoEpoch)) {
      mspAccessCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + MSP_ACCESS_CACHE_TTL_MS,
      });
    }
    return data;
  }).finally(() => {
    if (mspAccessInflight.get(cacheKey)?.request === request) mspAccessInflight.delete(cacheKey);
  });
  mspAccessInflight.set(cacheKey, { request, includeCustomers: params.includeCustomers });
  return request;
}

export function resetMSPAccessCacheForTests() {
  mspAccessCache.clear();
  mspAccessInflight.clear();
}

export function useMSPAccess(options?: UseMSPAccessOptions) {
  const { user, organization, isMspContext, isLoading: isOrgLoading } = useOrganization();
  const includeCustomers = options?.includeCustomers ?? true;
  const [mspAccess, setMspAccess] = useState<MSPAccessInfo>(createDefaultMSPAccess);
  const [isCheckingMSP, setIsCheckingMSP] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // Track the last org ID + mspContext combo we checked to avoid redundant queries
  const lastCheckKeyRef = useRef<string | null>(null);
  const mspAccessRef = useRef(mspAccess);
  // Only the latest check may write state, so a superseded read never overwrites a newer one.
  const requestSeqRef = useRef(0);

  const applyMSPAccess = useCallback((data: MSPAccessInfo) => {
    mspAccessRef.current = data;
    setMspAccess(data);
  }, []);

  // `background` refreshes without entering the loading state, so a logo write never blanks a page.
  const checkMSPAccess = useCallback(async (force = false, background = false) => {
    if (!user || !organization) return;

    const requestSeq = ++requestSeqRef.current;
    const isLatest = () => requestSeqRef.current === requestSeq;
    const cacheKey = buildMSPAccessCacheKey({
      userId: user.id,
      organizationId: organization.id,
      isMspContext,
      includeCustomers,
    });
    const cached = mspAccessCache.get(cacheKey);

    if (!force && cached && cached.expiresAt > Date.now()) {
      applyMSPAccess(cached.data);
      setHasChecked(true);
      setIsCheckingMSP(false);
      return;
    }

    try {
      if (!background) setIsCheckingMSP(true);
      if (force) {
        mspAccessCache.delete(cacheKey);
      }

      let accessInfo: MSPAccessInfo;
      let startedEpoch: number;
      do {
        startedEpoch = mspAccessLogoEpoch;
        accessInfo = await loadMSPAccessInfo(cacheKey, {
          userId: user.id,
          organizationId: organization.id,
          organizationName: organization.name,
          isMspContext,
          includeCustomers,
        });
      } while (includeCustomers && startedEpoch !== mspAccessLogoEpoch);

      if (isLatest()) applyMSPAccess(accessInfo);
    } catch (error) {
      console.error('Error checking MSP access:', error);
      // A logo write made the held marks unknown, so the failed refresh shows initials and the
      // logo error, never the previous mark.
      if (background && isLatest()) {
        applyMSPAccess({
          ...mspAccessRef.current,
          customers: withoutCustomerLogos(mspAccessRef.current.customers),
          customerLogoError: toCustomerLogoError(error),
        });
      }
    } finally {
      if (isLatest()) {
        setIsCheckingMSP(false);
        setHasChecked(true);
      }
    }
  }, [applyMSPAccess, includeCustomers, isMspContext, organization, user]);

  useEffect(() => {
    if (!includeCustomers) return undefined;
    const listener = (organizationId: string) => {
      if (includesCustomerOrganization(mspAccessRef.current, organizationId)) void checkMSPAccess(true, true);
    };
    mspAccessLogoWriteListeners.add(listener);
    return () => {
      mspAccessLogoWriteListeners.delete(listener);
    };
  }, [checkMSPAccess, includeCustomers]);

  useEffect(() => {
    if (user && organization) {
      const checkKey = `${organization.id}:${isMspContext}`;
      if (lastCheckKeyRef.current === checkKey) return;
      lastCheckKeyRef.current = checkKey;
      void checkMSPAccess();
    }
  }, [checkMSPAccess, isMspContext, organization, user]);

  // Loading is true if: org context is still loading, OR we have user/org but haven't checked MSP yet
  const isLoading = isOrgLoading || isCheckingMSP || (!!user && !!organization && !hasChecked);

  return { ...mspAccess, isLoading, refetch: () => checkMSPAccess(true) };
}
